export const pl = {
	meta: {
		title: 'Kamil Pfaff — IT Architect / Konsultant',
		description: 'Konsultacje, audyty i wdrożenia IT w całym cyklu życia oprogramowania: kod, infrastruktura, bezpieczeństwo i utrzymanie. Do tego mentoring 1:1. Kamil Pfaff, architekt IT z praktycznym doświadczeniem w programowaniu i DevOps.',
	},
	nav: {
		home: 'Kamil Pfaff, strona główna',
		index: 'Spis arkuszy',
		sheets: {
			cooperation: 'Współpraca',
			scope: 'Zakres',
			stack: 'Technologie',
			about: 'O mnie',
			contact: 'Kontakt',
		},
		book: 'Umów konsultację',
		menu: 'Menu',
		close: 'Zamknij menu',
		language: 'English version',
		languageShort: 'EN',
	},
	hero: {
		role: 'Architekt IT / Konsultant',
		heading: 'Od architektury, aż po produkcję.',
		lede: 'Konsultacje, audyty, wdrożenia i mentoring w całym cyklu życia oprogramowania: od pierwszej linii kodu, przez budowanie i wdrożenie, po produkcję i monitoring.',
		promise: 'Jedna osoba, która rozumie Twój system [od początku do końca] i odpowiada za każdą rekomendację.',
		revision: 'Zacznij tutaj',
		booking: {
			title: 'Zaproponuj termin rozmowy',
			day: 'Dzień',
			time: 'Pora',
			windows: ['8:00–12:00', '12:00–16:00', '16:00–20:00', '20:00–00:00'],
			submit: 'Umów konsultację',
			submitExternal: 'Wybierz godzinę w kalendarzu',
			note: 'Potwierdzę termin mailowo lub telefonicznie. Godziny w czasie polskim (Europe/Warsaw).',
			orDirect: 'Bezpośrednio?',
		},
		figure: {
			caption: 'Rys. 1',
			title: 'Cykl życia systemu',
			span: 'Od planu do monitoringu',
			pause: 'Zatrzymaj animację',
			play: 'Wznów animację',
		},
		phases: [
			{ name: 'Planowanie', line: 'Pomogę zaprojektować system i dobrać jego architekturę.' },
			{ name: 'Kod', line: 'Zaimplementuję rozwiązania, których potrzebujesz.' },
			{ name: 'Budowanie', line: 'Zautomatyzuję Twoje procesy.' },
			{ name: 'Wdrożenie', line: 'Skonteneryzuję Twoje środowisko.' },
			{ name: 'Utrzymanie', line: 'Zadbam o działanie Twojego systemu na produkcji.' },
			{ name: 'Monitoring', line: 'Wdrożę nowoczesny monitoring.' },
		],
		titleBlock: {
			project: 'Projekt',
			projectValue: 'Twój system',
			architect: 'Architekt',
			credentials: 'Certyfikacje',
			sheet: 'Arkusz',
			scale: 'Skala',
		},
	},
	cooperation: {
		heading: 'Trzy sposoby współpracy',
		intro: 'Zaczynamy od rozmowy. Dalej idziemy tak daleko, jak potrzebujesz.',
		first: 'Od tego zaczynamy',
		consult: {
			name: 'Konsultacja i audyt',
			audience: 'Dla każdego, kto ma problem w IT: lukę w bezpieczeństwie, niestabilny serwer, aplikację, która sypie błędami, albo vibecodowaną aplikację, która po prostu nie działa. Docieram do sedna problemu i znajduję właściwe rozwiązanie.',
			steps: [
				{ title: 'Problem', text: 'Opowiadasz, co nie działa i czego potrzebujesz.' },
				{ title: 'Diagnoza', text: 'Szukam przyczyny wszędzie tam, gdzie sięga problem.', areas: ['Kod', 'Konfiguracja', 'Infrastruktura', 'Bezpieczeństwo'] },
				{ title: 'Rozwiązanie', text: 'Dostajesz diagnozę, konkretne rozwiązanie i kolejne kroki.' },
			] as { title: string; text: string; areas?: string[] }[],
			cta: 'Umów konsultację',
		},
		mentor: {
			name: 'Mentoring i nauka',
			audience: 'Dla osób, które chcą wejść w DevOps, infrastrukturę lub programowanie albo uporządkować to, co już umieją. Zajęcia indywidualne.',
			topics: ['Linux i administracja', 'Docker i Kubernetes', 'Automatyzacja i CI/CD', 'Programowanie'],
			cta: 'Zobacz mentoring',
		},
		build: {
			name: 'Wdrożenie',
			audience: 'Gdy kierunek jest jasny, mogę go zrealizować: od naprawy jednego kontenera po pełną opiekę nad aplikacją na produkcji.',
			partners: 'Przy pełnych projektach, takich jak strony czy aplikacje webowe, współpracuję ze sprawdzonymi partnerami.',
			cta: 'Zobacz zakres prac',
		},
	},
	scope: {
		heading: 'Zakres prac',
		intro: 'Każda pozycja odpowiada fragmentowi pętli z rysunku 1. Wskaż pozycję, żeby zobaczyć, którego etapu dotyczy.',
		tools: 'Narzędzia',
		more: 'Szczegóły',
		less: 'Zwiń',
		items: [
			{
				name: 'Konsultacje IT',
				summary: 'Nie wiesz, jaką technologię wybrać? Pomogę dobrać architekturę i narzędzia do Twojego projektu, jego skali i budżetu.',
				details: ['Wybór między chmurą, serwerem dedykowanym, VPS a Twoimi maszynami.', 'Ocena istniejących rozwiązań i plan zmian.', 'Odpowiedzi na konkretne pytania Twojego zespołu.'],
				tools: [],
			},
			{
				name: 'Rozwiązania programistyczne',
				summary: 'Mam doświadczenie w programowaniu backendu. Napiszę brakujący element albo przeprowadzę Cię przez cały proces budowy aplikacji.',
				details: ['Backend w PHP (Symfony) i integracje.', 'Skrypty i narzędzia automatyzujące pracę zespołu.'],
				tools: ['Symfony', 'Git'],
			},
			{
				name: 'Automatyzacja CI/CD',
				summary: 'Koniec z ręcznymi wdrożeniami przez SSH. Aplikacja buduje się, testuje i wdraża automatycznie.',
				details: [
					'Krótszy czas przywracania (MTTR): wycofanie wdrożenia jednym kliknięciem.',
					'Częstsze wdrożenia dzięki prostej automatyzacji.',
					'Krótszy czas od zmiany do produkcji dzięki szybkiej informacji zwrotnej.',
					'Mniej nieudanych zmian: małe, dobrze przetestowane partie.',
				],
				tools: ['GitLab', 'Ansible', 'Docker', 'Kubernetes'],
			},
			{
				name: 'Konteneryzacja i Kubernetes',
				summary: 'Spakuję aplikację w kontenery, żeby działała tak samo wszędzie. Mam doświadczenie w migracji istniejących aplikacji do infrastruktury kontenerowej.',
				details: [
					'Skalowalność: dobrze przygotowaną aplikację łatwo skalować w Kubernetes lub OpenShift, co obniża koszty chmury przy zmiennym ruchu.',
					'Przenośność: kontener zawiera całe środowisko, więc uruchomienie sprowadza się do jednego polecenia.',
					'Łatwiejsze utrzymanie: obrazy można szybko podmienić, a orkiestrator sam restartuje i odtwarza aplikację po awarii.',
					'Pierwszy krok do automatyzacji: jednolite środowiska na każdym etapie.',
				],
				tools: ['Docker', 'Kubernetes', 'OpenShift'],
			},
			{
				name: 'Zarządzanie infrastrukturą',
				summary: 'Dobiorę serwery do Twoich potrzeb i zajmę się stabilnością, bezpieczeństwem i skalowaniem, żeby nie zaprzątało Ci to głowy.',
				details: ['Opieka nad serwerami Linux i zadania na wielu maszynach naraz.', 'Rozwiązanie dopasowane do skali: od prostej strony po klaster Kubernetes.'],
				tools: ['RHEL', 'AWS', 'Ansible'],
			},
			{
				name: 'Monitoring',
				summary: 'Błąd, awaria, zły kod odpowiedzi, podejrzany ruch? Dowiesz się o tym pierwszy.',
				details: ['Śledzenie błędów aplikacji w czasie rzeczywistym.', 'Metryki, alerty i czytelne dashboardy stabilności.'],
				tools: ['Sentry', 'Prometheus', 'Grafana'],
			},
		],
	},
	stack: {
		heading: 'Technologie',
		intro: 'Znam cały cykl życia oprogramowania: pisanie kodu, budowanie, wdrażanie, utrzymanie i monitoring. Poniżej narzędzia, których używam najczęściej i znam najlepiej.',
		columns: { item: 'Poz.', name: 'Technologia', role: 'Zastosowanie', note: 'Uwagi' },
		rows: [
			{ name: 'RHEL', role: 'Systemy serwerowe', note: 'Stabilne i bezpieczne serwery. Potwierdzone certyfikatami RHCSA i RHCE.' },
			{ name: 'Ansible', role: 'Automatyzacja', note: 'Automatyzacja konfiguracji, potwierdzona certyfikatem RHCE.' },
			{ name: 'Git', role: 'Kontrola wersji', note: 'Używa go każdy programista. Nie każdy dobrze.' },
			{ name: 'GitLab', role: 'Platforma DevOps', note: 'Również instancje self-managed. Filar moich transformacji DevOps.' },
			{ name: 'Docker', role: 'Konteneryzacja', note: 'Izolacja i przenośność aplikacji.' },
			{ name: 'Kubernetes', role: 'Orkiestracja', note: 'Skalowanie i niezawodne wdrożenia produkcyjne, także OpenShift.' },
			{ name: 'Sentry', role: 'Śledzenie błędów', note: 'Pokazuje błędy, które naprawdę mają znaczenie.' },
			{ name: 'Prometheus', role: 'Metryki i alerty', note: 'Zbiera metryki w bazie szeregów czasowych.' },
			{ name: 'Grafana', role: 'Wizualizacja', note: 'Czytelne dashboardy z danych Prometheusa i innych źródeł.' },
			{ name: 'AWS', role: 'Chmura', note: 'Skalowalna infrastruktura chmurowa.' },
			{ name: 'Symfony', role: 'Backend (PHP)', note: 'Ponad rok programowania aplikacji w tym frameworku.' },
		],
		polyglot: {
			label: 'Uwaga',
			text: 'To nie jest zamknięta lista. Programowałem w wielu językach i ten, w którym napisany jest Twój projekt, nie będzie dla mnie przeszkodą. Język to narzędzie, jak młotek: liczy się to, co budujesz.',
		},
		seals: {
			heading: 'Certyfikaty',
			issuer: 'Wydawca: Red Hat',
			rhcsa: 'Red Hat Certified System Administrator',
			rhce: 'Red Hat Certified Engineer',
		},
	},
	about: {
		heading: 'O mnie',
		paragraphs: [
			'Nazywam się Kamil Pfaff. Jestem architektem IT i konsultantem, a przy tym praktykiem. Znam cały cykl życia oprogramowania: piszę kod, buduję pipeline’y CI/CD, wdrażam, utrzymuję serwery i konfiguruję monitoring.',
			'Na co dzień pracuję z infrastrukturą opartą o Linux i kontenery (Kubernetes, OpenShift). Self-managed GitLab był filarem moich największych projektów, w tym pełnych transformacji DevOps i migracji aplikacji do kontenerów.',
			'Lubię proste rozwiązania prawdziwych problemów. Dlatego doradzam tylko to, co sam umiem zbudować i utrzymać.',
		],
		photoCaption: 'Fot. 1 — Kamil Pfaff',
		photoAlt: 'Kamil Pfaff',
		facts: [
			{ label: 'Rola', value: 'Architekt IT / Konsultant' },
			{ label: 'Doświadczenie', value: 'Programowanie, DevOps, administracja IT' },
			{ label: 'Uprawnienia', value: 'RHCSA, RHCE' },
			{ label: 'Siedziba', value: 'Pszczyna, Polska' },
		],
	},
	contact: {
		heading: 'Umów spotkanie',
		intro: 'Wybierz sposób, który Ci odpowiada: termin rozmowy, krótki brief albo telefon.',
		booking: {
			title: 'Konsultacja',
			text: 'Zaproponuj dzień i porę, a ja potwierdzę termin.',
			textExternal: 'Wybierz wolny termin w moim kalendarzu.',
			selected: 'Wybrany termin',
			change: 'Zmień w nagłówku',
		},
		brief: {
			title: 'Brief',
			text: 'Opisz krótko swój system lub problem. Odpowiem na podany adres.',
			name: 'Imię i nazwisko',
			email: 'E-mail',
			phone: 'Telefon (opcjonalnie)',
			topic: 'Czego dotyczy?',
			topics: ['Konsultacja / audyt', 'Mentoring', 'Wdrożenie', 'Inne'],
			preferred: 'Preferowany termin rozmowy',
			preferredNone: 'Nie wybrano',
			preferredPick: 'Wybierz',
			preferredChange: 'Zmień',
			message: 'Wiadomość',
			messagePlaceholder: 'Np. „Mamy aplikację w Dockerze na jednym VPS i ręczne wdrożenia. Chcemy to uporządkować.”',
			submit: 'Wyślij brief',
			sending: 'Wysyłanie…',
			invalid: 'Popraw zaznaczone pola, zanim wyślesz.',
			success: 'Dziękuję, brief dotarł.',
			successNext: 'Odezwę się na podany adres e-mail.',
			sendAnother: 'Wyślij kolejny',
			error: 'Nie udało się wysłać formularza. Spróbuj ponownie albo napisz bezpośrednio na',
			errors: {
				name: 'Podaj imię i nazwisko.',
				email: 'Podaj poprawny adres e-mail, np. jan@firma.pl.',
				phone: 'Podaj poprawny numer, np. +48 600 000 000.',
				message: 'Napisz kilka słów o tym, w czym mogę pomóc.',
			},
		},
		direct: {
			title: 'Bezpośrednio',
			call: 'Zadzwoń',
			write: 'Napisz',
		},
	},
	mentoring: {
		meta: {
			title: 'Mentoring IT 1:1 — Linux, Docker, Kubernetes, DevOps | Kamil Pfaff',
			description: 'Indywidualny mentoring i nauka: Linux, Docker, Kubernetes, Ansible, CI/CD, programowanie i podstawy cyberbezpieczeństwa. Przegląd kodu i audyt Twojej infrastruktury. Kamil Pfaff, RHCSA, RHCE.',
		},
		nav: {
			topics: 'Tematy',
			review: 'Przegląd',
			process: 'Przebieg',
			mentor: 'Mentor',
			cta: 'Wyślij zapytanie',
		},
		hero: {
			heading: 'Od pierwszej linijki kodu po produkcję.',
			lede: 'Mentoring 1:1 z programowania, linuksa, kontenerów czy automatyzacji. Porozmawiamy, uzupełnimy braki, przejrzę Twój kod albo serwer i powiem wprost, nad czym należy się pochylić. Opisz, czego potrzebujesz, a ułożymy konkretny plan działania.',
			modesLegend: 'Czego potrzebujesz?',
			modes: [
				{ name: 'Nauka technologii', line: 'Programowanie, Linux, Docker, Kubernetes, Ansible. Od podstaw albo od miejsca, w którym utknąłeś.' },
				{ name: 'Code Review', line: 'Przeglądu kodu Twojej aplikacji, Dockerfile\'a, pipeline\'a czy playbook\'a połączony z dyskusją 1:1. Omówimy słabe punkty, przeanalizujemy błędy i w razie potrzeby - wspólnie wdrożymy poprawki.' },
				{ name: 'Audyt infrastruktury', line: 'VPS, homelab albo serwer projektu: bezpieczeństwo, kopie zapasowe, konfiguracja, wdrożenia.' },
				{ name: 'Przygotowanie do egzaminu', line: 'Przygotowujesz się do certyfikacji, a może potrzebujesz zaliczyć egzamin na studiach? Przejrzymy Twoją wiedzę, ułożymy plan nauki i razem przejdziemy przez ten proces.' },
			],
		},
		rfi: {
			title: 'Zapytanie',
			regarding: 'Dotyczy',
			revision: 'Zacznij tutaj',
			topics: 'Tematy',
			topicsHint: 'Zaznacz dowolne',
			topicList: ['Linux', 'Bash', 'Git', 'Docker', 'Kubernetes', 'Ansible', 'CI/CD', 'Monitoring', 'Cyberbezpieczeństwo', 'Sieci', 'Programowanie', 'C#', 'Java', 'JS', 'PHP', 'Inne'],
			level: 'Twój poziom',
			levels: ['Zaczynam', 'Technikum / Studia', 'Znam podstawy', 'Używam w pracy'],
			goal: 'Co chcesz osiągnąć?',
			goalPlaceholder: 'Np. „Chcę postawić swoją aplikację w Dockerze na VPS i rozumieć, co robię.”',
			known: 'Co już umiesz? (opcjonalnie)',
			knownPlaceholder: 'Np. „Piszę w Pythonie, terminala używam tylko do gita.”',
			link: 'Link do repozytorium lub opis środowiska (opcjonalnie)',
			linkPlaceholder: 'https://github.com/… albo „VPS z Ubuntu, Nginx, dwie aplikacje”',
			name: 'Imię',
			email: 'E-mail',
			phone: 'Telefon (opcjonalnie)',
			submit: 'Wyślij zapytanie',
			sending: 'Wysyłanie…',
			note: 'Zajęcia online, 1:1. Zakres, termin i cenę ustalimy po Twoim zapytaniu.',
			success: 'Dziękuję, zapytanie dotarło. Odpiszę z propozycją na podany adres.',
			sendAnother: 'Wyślij kolejne',
			error: 'Nie udało się wysłać zapytania. Spróbuj ponownie albo napisz bezpośrednio na',
			errors: {
				name: 'Podaj imię.',
				email: 'Podaj poprawny adres e-mail, np. jan@poczta.pl.',
				phone: 'Podaj poprawny numer, np. +48 600 000 000.',
				goal: 'Napisz kilka słów o tym, czego chcesz się nauczyć lub co mam przejrzeć.',
			},
			summary: {
				request: 'Rodzaj',
				topics: 'Tematy',
				level: 'Poziom',
				goal: 'Cel',
				known: 'Umie',
				link: 'Link / środowisko',
			},
		},
		topics: {
			heading: 'Tematy',
			intro: 'Uczę tego, czego używam na co dzień. Zajęcia układam pod Twój poziom i Twój projekt, nie pod gotowy kurs.',
			columns: { item: 'Poz.', name: 'Temat', scope: 'Co przerabiamy', basis: 'Podstawa' },
			practice: 'Praktyka',
			rows: [
				{ name: 'Linux i powłoka', scope: 'Użytkownicy i uprawnienia, systemd, pakiety, logi, sieć, skrypty w Bashu.', basis: 'RHCSA' },
				{ name: 'Git', scope: 'Gałęzie, merge i rebase, czytelna historia, praca w zespole, naprawianie pomyłek.', basis: '' },
				{ name: 'Docker', scope: 'Obrazy i Dockerfile, Compose, sieci i wolumeny, lekkie i bezpieczne obrazy.', basis: '' },
				{ name: 'Kubernetes', scope: 'Pody, deploymenty, serwisy, ingress, konfiguracja i sekrety, OpenShift.', basis: '' },
				{ name: 'Ansible', scope: 'Playbooki, role, inwentarze, idempotencja, automatyzacja wielu serwerów.', basis: 'RHCE' },
				{ name: 'CI/CD', scope: 'Pipeline’y w GitLabie, testy, budowanie obrazów, automatyczne wdrożenia.', basis: '' },
				{ name: 'Monitoring', scope: 'Metryki w Prometheusie, dashboardy w Grafanie, śledzenie błędów w Sentry, alerty.', basis: '' },
				{ name: 'Podstawy cyberbezpieczeństwa', scope: 'SSH i klucze, firewall, aktualizacje, sekrety, najmniejsze uprawnienia, kopie zapasowe.', basis: '' },
				{ name: 'Programowanie', scope: 'Podstawy, backend w PHP (Symfony), skrypty automatyzujące, czytelny kod.', basis: '' },
			],
		},
		review: {
			heading: 'Tak wygląda Code Review',
			intro: 'Nie dostajesz ogólnika „jest OK”. Każda uwaga ma numer, miejsce w kodzie, powód i poprawkę. Tak samo przeglądam pipeline’y, playbooki i konfigurację serwerów.',
			caption: 'Rys. 1',
			title: 'Przykładowy przegląd pliku docker-compose.yml',
			sample: 'Przykład',
			notes: [
				{ title: 'Wersja „latest” zmienia się bez Twojej wiedzy', fix: 'Przypnij wersję, np. postgres:16. Aktualizacja to wtedy Twoja decyzja, a nie niespodzianka po restarcie.' },
				{ title: 'Hasło w pliku trafia do repozytorium', fix: 'Przenieś je do pliku .env lub dedykowanych sekretów, dodaj .env do .gitignore i koniecznie zmień hasło - to jest już skompromitowane.' },
				{ title: 'Baza danych ma otwarte porty', fix: 'Usuń mapowanie portu 5432. Aplikacja łączy się z bazą przez wewnętrzną sieć - dodatkowe wystawione porty to potencjalne wektory ataku.' },
				{ title: 'Po restarcie serwera nic nie wstanie', fix: 'Dodaj restart: unless-stopped i healthcheck, żeby kontenery wracały same.' },
			],
		},
		process: {
			heading: 'Jak to przebiega',
			steps: [
				{ name: 'Zapytanie', line: 'Wysyłasz formularz z góry strony: czego potrzebujesz i na jakim jesteś etapie.' },
				{ name: 'Propozycja', line: 'Odpisuję z planem: zakres, forma, termin i cena. Nic nie jest wiążące, dopóki się nie zgodzisz.' },
				{ name: 'Sesja', line: 'Spotykamy się online, 1:1, z udostępnianiem ekranu. Pracujemy na Twoim kodzie i Twoich serwerach.' },
				{ name: 'Notatki', line: 'Dostajesz podsumowanie, uwagi i kolejne kroki, żeby dalej iść samodzielnie.' },
			],
			facts: [
				{ label: 'Forma', value: 'Online, 1:1' },
				{ label: 'Spotkanie', value: 'Udostępnianie ekranu' },
				{ label: 'Język', value: 'Polski lub angielski' },
				{ label: 'Cena', value: 'Ustalana po zapytaniu' },
			],
		},
		mentor: {
			heading: 'Kto uczy',
			paragraphs: [
				'Nazywam się Kamil Pfaff. Jestem architektem IT i inżynierem DevOps. Na co dzień pracuję z infrastrukturą opartą o Linux i kontenery, automatyzacją i CI/CD.',
				'Uczę tego samego, co wdrażam u klientów, więc zajęcia opierają się na prawdziwych problemach, a nie na slajdach. Moją znajomość Linuksa i Ansible potwierdzają certyfikaty Red Hat.',
			],
			business: 'Prowadzisz firmę i potrzebujesz konsultacji lub wdrożenia?',
			businessLink: 'Zobacz ofertę dla firm',
			direct: 'Wolisz napisać lub zadzwonić?',
		},
	},
	footer: {
		companyLabel: 'Firma',
		company: 'Kamil Pfaff Konsulting IT',
		address: 'Kasztelańska 4, 43-200 Pszczyna',
		revision: 'Rewizja',
		revisionValue: 'B — nowa wersja strony',
		top: 'Do góry',
	},
};

export type Copy = typeof pl;
